import { roundNumber } from './rounding';

function toNumber(value, fallback = 0) {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : fallback;
}

function round(value, digits = 2) {
  return roundNumber(value, digits);
}

function metricValue(metric) {
  if (metric && typeof metric === 'object' && 'value' in metric) {
    return toNumber(metric.value);
  }

  return toNumber(metric);
}

function metricDetails(metric) {
  if (metric && typeof metric === 'object' && 'value' in metric) {
    return {
      value: metricValue(metric),
      trendDirection: metric.trendDirection ?? null,
      trendPercent: metric.trendPercent == null ? null : toNumber(metric.trendPercent, null),
    };
  }

  return {
    value: metricValue(metric),
    trendDirection: null,
    trendPercent: null,
  };
}

export function normalizePersonalOrdersSummary(summary = null) {
  if (!summary || typeof summary !== 'object') return null;

  return {
    revenue: metricDetails(summary.revenue),
    checksCount: metricDetails(summary.checksCount),
    averageCheck: metricDetails(summary.averageCheck),
    bonuses: metricDetails(summary.bonuses),
    guestRating: metricDetails(summary.guestRating),
  };
}

function isMergeDebugEnabled() {
  if (typeof window === 'undefined') return false;

  try {
    return ['employeeAnalyticsMergeDebug', 'employeeAnalyticsRefsDebug'].some(
      (key) => window.localStorage?.getItem(key) === '1',
    );
  } catch {
    return false;
  }
}

function uniqTitles(values = []) {
  const unique = [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean).filter((v) => v !== '—'))];
  return unique.length ? unique.join(', ') : '—';
}

function extractExplicitPointTitles(row = {}) {
  return [
    row?.pointTitle,
    ...(Array.isArray(row?.pointTitles) ? row.pointTitles : []),
  ];
}

function extractPointIds(row = {}) {
  const rawPointIds = Array.isArray(row?.pointIds)
    ? row.pointIds
    : Array.isArray(row?.points)
      ? row.points
      : [];
  const fromList = rawPointIds
    .map((point) => String(point?.id ?? point?.pointId ?? point ?? ''))
    .map((value) => value.trim())
    .filter(Boolean);
  const pointId = String(row?.pointId ?? '').trim();
  return fromList.length ? fromList : pointId ? [pointId] : [];
}

function getEmployeeLookupIds(row = {}) {
  return [
    row?.employeeId,
    row?.personalId,
    row?.employeeExternalId,
    row?.externalPersonalId,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
}

function resolveEmployee(row = {}, refs = {}) {
  const ids = getEmployeeLookupIds(row);
  const foundId = ids.find((id) => refs?.employeesById?.[id]);

  return foundId ? refs.employeesById[foundId] : null;
}

function resolvePointTitle(row = {}, refs = {}) {
  const explicit = uniqTitles(extractExplicitPointTitles(row));
  if (explicit !== '—') return explicit;

  const pointIds = extractPointIds(row);
  const titlesFromIds = pointIds.map((id) => refs?.pointsById?.[id]?.title).filter(Boolean);
  if (titlesFromIds.length) return uniqTitles(titlesFromIds);

  const employee = resolveEmployee(row, refs);
  if (employee && typeof employee === 'object') {
    const titlesFromEmployeeIds = (employee.pointIds || [])
      .map((id) => refs?.pointsById?.[String(id)]?.title)
      .filter(Boolean);
    if (titlesFromEmployeeIds.length) return uniqTitles(titlesFromEmployeeIds);
    if (Array.isArray(employee.pointTitles) && employee.pointTitles.length) return uniqTitles(employee.pointTitles);
  }

  return '—';
}

function debugSummaryMerge(rows = [], normalizedRows = [], refs = {}) {
  if (!isMergeDebugEnabled()) return;

  const diagnostics = rows
    .map((row, index) => {
      const lookupIds = getEmployeeLookupIds(row);
      const matchedId = lookupIds.find((id) => refs?.employeesById?.[id]) || '';
      const normalized = normalizedRows[index] || {};

      return {
        index,
        lookupIds: lookupIds.join(', '),
        matchedId,
        hasEmployeeRef: Boolean(matchedId),
        rawEmployeeName: row?.employeeName || row?.personalName || '',
        resolvedEmployeeName: normalized.employeeName,
        rawPointId: row?.pointId || '',
        rawPointIds: Array.isArray(row?.pointIds) ? row.pointIds.join(', ') : '',
        rawPointTitle: row?.pointTitle || '',
        rawPointTitles: Array.isArray(row?.pointTitles) ? row.pointTitles.join(', ') : '',
        resolvedPointTitle: normalized.pointTitle,
        revenue: normalized.revenue,
        checksCount: normalized.checksCount,
      };
    })
    .filter((item) => !item.hasEmployeeRef || item.resolvedPointTitle === '—');

  if (!diagnostics.length) return;

  console.groupCollapsed('[employeeAnalyticsMerge] unresolved summary rows');
  console.table(diagnostics);
  console.log('employeesById keys sample', Object.keys(refs?.employeesById || {}).slice(0, 30));
  console.log('pointsById keys sample', Object.keys(refs?.pointsById || {}).slice(0, 30));
  console.groupEnd();
}

export function normalizePersonalOrdersSummaryRows(rows = [], totalRevenueOverride = null, refs = {}) {
  if (!Array.isArray(rows)) return [];

  const normalized = rows.map((row, index) => {
    const employeeId = row?.employeeId || row?.personalId || '';
    const employee = resolveEmployee(row, refs);
    const revenue = toNumber(row?.revenue);
    const checksCount = toNumber(row?.checksCount ?? row?.ordersTotal);
    const averageCheck =
      row?.averageCheck == null && checksCount ? round(revenue / checksCount) : toNumber(row?.averageCheck);

    return {
      rank: toNumber(row?.rank, index + 1),
      employeeId,
      employeeName: row?.employeeName || row?.personalName || employee?.employeeName || 'Неизвестный сотрудник',
      employeeExternalId: row?.employeeExternalId || employee?.employeeExternalId || null,
      pointId: row?.pointId || null,
      pointTitle: resolvePointTitle(row, refs),
      revenue,
      revenueShare: toNumber(row?.revenueShare),
      checksCount,
      averageCheck,
      bonuses: toNumber(row?.bonuses),
      bonusShare: toNumber(row?.bonusShare),
      guestRating: row?.guestRating == null ? null : toNumber(row.guestRating, null),
    };
  });

  debugSummaryMerge(rows, normalized, refs);

  const totalRevenue =
    toNumber(totalRevenueOverride) || normalized.reduce((sum, row) => sum + toNumber(row.revenue), 0);

  return normalized.map((row) => ({
    ...row,
    bonusShare: totalRevenue ? round((row.bonuses / totalRevenue) * 100, 1) : 0,
  }));
}

function normalizeLegacyPersonalOrdersSummaryRows(rows = [], refs = {}) {
  if (!Array.isArray(rows)) return [];

  const normalized = rows.map((row) => {
    const employee = resolveEmployee(row, refs) || {};
    const revenue = toNumber(row?.resultSumTotal);
    const checksCount = toNumber(row?.ordersTotal);
    const bonuses = toNumber(row?.loyaltyBonusTotal);
    const ratedOrdersTotal = toNumber(row?.ratedOrdersTotal);
    const rankTotal = toNumber(row?.rankTotal);

    return {
      employeeId: row?.personalId || '',
      employeeName: employee?.employeeName || row?.personalName || row?.personalId || 'Неизвестный сотрудник',
      employeeExternalId: employee?.employeeExternalId || null,
      pointId: row?.pointId || null,
      pointTitle: resolvePointTitle(row, refs),
      revenue,
      revenueShare: 0,
      checksCount,
      averageCheck: checksCount ? round(revenue / checksCount) : 0,
      bonuses,
      bonusShare: 0,
      guestRating: ratedOrdersTotal ? round(rankTotal / ratedOrdersTotal, 1) : null,
    };
  });

  debugSummaryMerge(rows, normalized, refs);

  const totalRevenue = normalized.reduce((sum, row) => sum + toNumber(row.revenue), 0);
  return normalized
    .sort((left, right) => right.revenue - left.revenue)
    .map((row, index) => ({
      ...row,
      rank: index + 1,
      revenueShare: totalRevenue ? round((row.revenue / totalRevenue) * 100, 1) : 0,
      bonusShare: totalRevenue ? round((row.bonuses / totalRevenue) * 100, 1) : 0,
    }));
}

function buildLegacyPersonalOrdersSummary(rows = []) {
  if (!Array.isArray(rows) || !rows.length) return null;

  const revenue = rows.reduce((sum, row) => sum + toNumber(row?.resultSumTotal), 0);
  const checksCount = rows.reduce((sum, row) => sum + toNumber(row?.ordersTotal), 0);
  const bonuses = rows.reduce((sum, row) => sum + toNumber(row?.loyaltyBonusTotal), 0);
  const ratings = rows
    .map((row) => {
      const ratedOrdersTotal = toNumber(row?.ratedOrdersTotal);
      if (!ratedOrdersTotal) return null;
      return toNumber(row?.rankTotal) / ratedOrdersTotal;
    })
    .filter((value) => value != null);

  return {
    revenue: round(revenue),
    checksCount,
    averageCheck: checksCount ? round(revenue / checksCount) : 0,
    bonuses: round(bonuses),
    guestRating: ratings.length ? round(ratings.reduce((sum, value) => sum + value, 0) / ratings.length) : 0,
  };
}

function extractRows(payload = {}) {
  if (Array.isArray(payload)) return payload;

  const rowFields = [
    payload?.rows,
    payload?.items,
    payload?.employees,
    payload?.personals,
    payload?.ratingRows,
    payload?.personalRows,
  ];

  return rowFields.find((rows) => Array.isArray(rows)) || [];
}

function hasLegacySummaryRowShape(rows = []) {
  return rows.some(
    (row) =>
      Object.prototype.hasOwnProperty.call(row || {}, 'ordersTotal') ||
      Object.prototype.hasOwnProperty.call(row || {}, 'resultSumTotal') ||
      Object.prototype.hasOwnProperty.call(row || {}, 'loyaltyBonusTotal'),
  );
}

export function toEmployeeAnalyticsSummary(response = {}, refs = {}) {
  const data = response?.data;
  const payload =
    Array.isArray(response) || response?.summary || response?.rows
      ? response
      : Array.isArray(data?.data) || data?.data?.summary || data?.data?.rows
        ? data.data
        : data;
  const rows = extractRows(payload);
  const isLegacyRows = hasLegacySummaryRowShape(rows);

  if (Array.isArray(payload) || isLegacyRows) {
    return {
      summary: normalizePersonalOrdersSummary(payload?.summary || buildLegacyPersonalOrdersSummary(rows)),
      rows: normalizeLegacyPersonalOrdersSummaryRows(rows, refs),
    };
  }

  if (!payload || typeof payload !== 'object') {
    return {
      summary: null,
      rows: [],
    };
  }

  return {
    summary: normalizePersonalOrdersSummary(payload.summary),
    rows: normalizePersonalOrdersSummaryRows(rows, metricValue(payload.summary?.revenue), refs),
  };
}
